import { useEffect, useState } from 'react';
import { ExternalLink, MapPin, Pencil } from 'lucide-react';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';

const severityStyles = {
  critical: 'border-red-500/25 bg-red-500/10 text-red-200',
  warning: 'border-amber-500/25 bg-amber-500/10 text-amber-100',
  healthy: 'border-emerald-500/25 bg-emerald-500/10 text-emerald-100'
};

const severityLabels = {
  critical: 'Critical',
  warning: 'Needs Attention',
  healthy: 'Healthy'
};

const getLocationName = (location) => location?.name || location?.boardName || 'Location';

const getProfileForm = (location) => ({
  mondayBoardId: location?.mondayBoardId || '',
  mondayBoardUrl: location?.mondayBoardUrl || '',
  operationsMondayItemId: location?.operationsMondayItemId || '',
  notes: location?.notes || ''
});

const getValidMondayBoardUrl = (value) => {
  const trimmedValue = value?.trim();
  if (!trimmedValue) return '';

  try {
    const url = new URL(trimmedValue);
    const hostname = url.hostname.toLowerCase();
    const isMondayHost = hostname === 'monday.com' || hostname.endsWith('.monday.com');
    return url.protocol === 'https:' && isMondayHost ? trimmedValue : '';
  } catch (error) {
    return '';
  }
};

export default function LocationProfileHeader({ location, statusSummary, isAdmin, updateProfile }) {
  const authenticatedFetch = useAuthenticatedFetch();
  const [photoState, setPhotoState] = useState({ status: 'empty', url: null });
  const [isEditing, setIsEditing] = useState(false);
  const [profileForm, setProfileForm] = useState(getProfileForm(location));
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  useEffect(() => {
    if (!isEditing) setProfileForm(getProfileForm(location));
  }, [isEditing, location]);

  useEffect(() => {
    let isMounted = true;
    let objectUrl = null;

    if (!location?.primaryPhotoAssetId) {
      setPhotoState({ status: 'empty', url: null });
      return undefined;
    }

    setPhotoState({ status: 'loading', url: null });

    const loadPhoto = async () => {
      try {
        const response = await authenticatedFetch(`/api/locations/assets/${location.primaryPhotoAssetId}`);
        if (!response.ok) throw new Error('Location photo request failed.');
        const blob = await response.blob();
        objectUrl = URL.createObjectURL(blob);

        if (isMounted) {
          setPhotoState({ status: 'loaded', url: objectUrl });
        } else {
          URL.revokeObjectURL(objectUrl);
        }
      } catch (error) {
        if (isMounted) setPhotoState({ status: 'failed', url: null });
      }
    };

    loadPhoto();

    return () => {
      isMounted = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [authenticatedFetch, location?.primaryPhotoAssetId]);

  if (!location) return null;

  const locationName = getLocationName(location);
  const severity = statusSummary?.highestSeverity || 'healthy';
  const showPhoto = photoState.status === 'loaded' && Boolean(photoState.url);
  const mondayBoardUrl = getValidMondayBoardUrl(location.mondayBoardUrl);
  const handleProfileFieldChange = (event) => {
    const { name, value } = event.target;
    setProfileForm(prev => Object.assign({}, prev, { [name]: value }));
  };
  const handleCancelEdit = () => {
    setProfileForm(getProfileForm(location));
    setSaveError('');
    setIsEditing(false);
  };
  const handleSaveProfile = async (event) => {
    event.preventDefault();
    if (!updateProfile) return;
    const mondayBoardUrl = getValidMondayBoardUrl(profileForm.mondayBoardUrl);
    if (profileForm.mondayBoardUrl.trim() && !mondayBoardUrl) {
      setSaveError('Enter a valid monday.com HTTPS board URL.');
      return;
    }
    setSaving(true);
    setSaveError('');
    try {
      await updateProfile(Object.assign({}, profileForm, { mondayBoardUrl }));
      setIsEditing(false);
    } catch (error) {
      setSaveError(error.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <header className="overflow-hidden rounded-lg border border-white/10 bg-[#0b0b0b]">
      <div className="grid gap-0 lg:grid-cols-[22rem_1fr]">
        <div className="relative h-56 bg-black lg:h-full">
          {showPhoto ? (
            <img
              src={photoState.url}
              alt={`${locationName} location`}
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="flex h-full flex-col items-center justify-center gap-2 bg-[#111] text-gray-500">
              <MapPin className="h-9 w-9 text-gray-600" aria-hidden="true" />
              <span className="text-xs font-semibold uppercase tracking-widest">No location photo</span>
            </div>
          )}
        </div>

        <div className="flex min-w-0 flex-col justify-between gap-5 p-5">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <h1 className="text-2xl font-semibold leading-tight text-white">{locationName}</h1>
              {location.address && (
                <p className="mt-2 flex items-center gap-2 text-sm text-gray-400">
                  <MapPin className="h-4 w-4 shrink-0 text-gray-500" aria-hidden="true" />
                  <span>{location.address}</span>
                </p>
              )}
            </div>
            <div className="flex items-center gap-2">
              {isAdmin && (
                <button
                  type="button"
                  aria-label="Edit location profile"
                  onClick={() => setIsEditing(true)}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-white/10 bg-white/[0.04] text-gray-300 transition-colors hover:bg-white/[0.08] hover:text-white"
                >
                  <Pencil className="h-4 w-4" aria-hidden="true" />
                </button>
              )}
              <span className={`rounded-full border px-3 py-1 text-xs font-bold uppercase tracking-wider ${severityStyles[severity]}`}>
                {severityLabels[severity]}
              </span>
            </div>
          </div>

          <dl className="grid gap-3 text-sm sm:grid-cols-3">
            <div className="rounded-lg border border-white/10 bg-white/[0.03] p-3">
              <dt className="text-xs uppercase tracking-widest text-gray-500">Active Issues</dt>
              <dd className="mt-1 text-lg font-semibold text-white">{statusSummary?.issueCount || 0}</dd>
            </div>
            <div className="rounded-lg border border-white/10 bg-white/[0.03] p-3">
              <dt className="text-xs uppercase tracking-widest text-gray-500">Last Sync</dt>
              <dd className="mt-1 truncate text-sm font-semibold text-white">{location.lastSyncedAt || 'Not synced'}</dd>
            </div>
            <div className="rounded-lg border border-white/10 bg-white/[0.03] p-3">
              <dt className="text-xs uppercase tracking-widest text-gray-500">Access</dt>
              <dd className="mt-1 text-sm font-semibold text-white">{isAdmin ? 'Admin view' : 'Standard view'}</dd>
            </div>
          </dl>

          <div className="flex flex-wrap items-center gap-2">
            {mondayBoardUrl ? (
              <a
                href={mondayBoardUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-lg border border-white/10 bg-white px-3 py-2 text-xs font-semibold text-black transition-colors hover:bg-gray-200"
              >
                Monday Board
                <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
              </a>
            ) : (
              <span className="rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-xs font-semibold text-gray-500">
                Monday board unavailable
              </span>
            )}
          </div>

          {location.notes && !isEditing && (
            <p className="text-sm text-gray-400">{location.notes}</p>
          )}

          {isEditing && (
            <form onSubmit={handleSaveProfile} className="grid gap-3 rounded-lg border border-white/10 bg-white/[0.03] p-3 text-sm sm:grid-cols-2">
              <label className="grid gap-1 text-xs font-semibold uppercase tracking-widest text-gray-500">
                Monday Board ID
                <input
                  name="mondayBoardId"
                  value={profileForm.mondayBoardId}
                  onChange={handleProfileFieldChange}
                  className="rounded-lg border border-white/10 bg-black px-3 py-2 text-sm font-normal text-white outline-none focus:border-white/30"
                />
              </label>
              <label className="grid gap-1 text-xs font-semibold uppercase tracking-widest text-gray-500">
                Monday Board URL
                <input
                  name="mondayBoardUrl"
                  value={profileForm.mondayBoardUrl}
                  onChange={handleProfileFieldChange}
                  className="rounded-lg border border-white/10 bg-black px-3 py-2 text-sm font-normal text-white outline-none focus:border-white/30"
                />
              </label>
              <label className="grid gap-1 text-xs font-semibold uppercase tracking-widest text-gray-500 sm:col-span-2">
                Operations Item ID
                <input
                  name="operationsMondayItemId"
                  value={profileForm.operationsMondayItemId}
                  onChange={handleProfileFieldChange}
                  className="rounded-lg border border-white/10 bg-black px-3 py-2 text-sm font-normal text-white outline-none focus:border-white/30"
                />
              </label>
              <label className="grid gap-1 text-xs font-semibold uppercase tracking-widest text-gray-500 sm:col-span-2">
                Notes
                <textarea
                  name="notes"
                  value={profileForm.notes}
                  onChange={handleProfileFieldChange}
                  rows={3}
                  className="rounded-lg border border-white/10 bg-black px-3 py-2 text-sm font-normal text-white outline-none focus:border-white/30"
                />
              </label>
              {saveError && <p className="text-xs text-red-200 sm:col-span-2">{saveError}</p>}
              <div className="flex flex-wrap gap-2 sm:col-span-2">
                <button
                  type="submit"
                  disabled={saving}
                  className="rounded-lg bg-white px-3 py-2 text-xs font-semibold text-black transition-colors hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {saving ? 'Saving...' : 'Save'}
                </button>
                <button
                  type="button"
                  onClick={handleCancelEdit}
                  className="rounded-lg border border-white/10 px-3 py-2 text-xs font-semibold text-gray-300 transition-colors hover:bg-white/[0.06] hover:text-white"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </header>
  );
}
